import React, { useState } from "react"
import { createUseStyles } from "react-jss"
import { Anchor } from "../app/components/anchor"
import { useRouter } from "next/router"
import Head from "next/head"
import ErrorPage from "next/error"
import Footer from "../storyblok/static/Footer"
import Navbar from "../components/navbar"
import ShipmentService from "../services/shipment"
import { fontSize, fontWeight } from "../styles/fonts-constants"
import colors from "../styles/colors"
import StarRating from "../components/StarRating"
import { mediaQueries } from "../styles/breakpoints"
import PageImage from "../assets/images/og_default.jpg"
import TrustpilotGoogleReviews from "../components/TrustpilotGoogleReviews"
import ReviewsContextProvider from "../context/ReviewsContext/ReviewsContextProvider"
import * as config from "../util/config"
import { isValidDriverId } from "../util/validateDriver.ts"
import LazyYoutube from "../components/testimonials/LazyYoutube"

const useStyles = createUseStyles({
  heading: {
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: "bold",
    fontSize: 25,
    lineHeight: "30px",
    textTransform: "capitalize",
    paddingLeft: 16,
    paddingRight: 16,
    marginBottom: 25,
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
    [mediaQueries.largeMobile]: {
      paddingLeft: 0,
      paddingRight: 0,
      fontSize: 35,
      marginBottom: 45,
      lineHeight: "55px",
    },
  },
  subHeading: {
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: fontWeight.extraBold,
    fontSize: 24,
    lineHeight: "30px",
    paddingLeft: 16,
    paddingRight: 16,
    marginTop: 25,
    marginBottom: 25,
    maxWidth: 600,
    width: "90%",
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
    [mediaQueries.largeMobile]: {
      paddingLeft: 0,
      paddingRight: 0,
      fontSize: 28,
      marginBottom: 45,
    },
  },
  shipmentTitle: {
    fontFamily: "'Roboto', sans-serif",
    fontWeight: fontWeight.bold,
    fontSize: fontSize.medium,
    color: colors.blue9,
    margin: "0px 0px 8px 0px",
    "&:hover": {
      color: colors.blue9,
    },
  },
  mainWrapper: {
    paddingTop: 70,
    paddingBottom: 70,
    [mediaQueries.tablet]: {
      paddingTop: 150,
      paddingBottom: 150,
    },
  },
  card: {
    background: "white",
    width: "100%",
    margin: 0,
    padding: 24,
    boxShadow: "0px 15px 75px rgba(6, 17, 46, 0.15);",
    marginBottom: 30,
    [mediaQueries.largeMobile]: {
      borderRadius: 12,
    },
  },
  reviews: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
  },
  reviewsInnerWrapper: {
    maxWidth: 600,
    width: "100%",
  },
  text: {
    fontWeight: fontWeight.normal,
    fontSize: fontSize.extraSmall,
    lineHeight: "26px",
    marginBottom: 16,
    marginTop: 8,
  },
  overlineText: {
    marginBottom: 0,
    color: colors.gray8,
    paddingTop: 2,
    [mediaQueries.largeMobile]: {
      marginLeft: 8,
    },
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    padddingLeft: 16,
    paddingRight: 16,
    [mediaQueries.largeMobile]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  paginationButton: {
    display: "inline-block",
    color: "white",
    fontSize: fontSize.small,
    lineHeight: "36px",
    fontWeight: fontWeight.normal,
    height: 36,
    backgroundColor: colors.blue8,
    borderRadius: 3,
    paddingLeft: 12,
    paddingRight: 12,
    maxWidth: 600,
    minWidth: 120,
    textAlign: "center",
    "&:hover": {
      backgroundColor: colors.blue19,
      color: "white",
    },
  },
  overline: {
    flexDirection: "column",
    display: "flex",
    marginBottom: 8,
    [mediaQueries.largeMobile]: {
      flexDirection: "row",
    },
  },
  priceTag: {
    fontFamily: "var(--font-montserrat), sans-serif",
  },
  driverName: {
    colors: colors.blue9,
  },
  ctaIntroText: {
    fontSize: "18px",
    textAlign: "center",
    marginBottom: "20px",
  },
  ctaOutroText: {
    fontSize: "16px",
    textAlign: "center",
    marginTop: "5px",
    marginBottom: "20px",
  },
  ctaContainer: {
    textAlign: "center",
    marginBottom: "20px",
  },
  ctaButton: {
    display: "inline-block",
    padding: "12px 24px",
    backgroundColor: colors.blue8,
    color: "white",
    textDecoration: "none",
    borderRadius: "4px",
    fontWeight: fontWeight.bold,
    "&:hover": {
      backgroundColor: colors.blue19,
      color: "white",
    },
  },
  headerImage: {
    maxWidth: "100%",
    height: "auto",
    display: "block",
    margin: "0 auto",
  },
  headerImageContainer: {
    maxWidth: 600,
    margin: "0 auto",
  },
  [mediaQueries.smallerThanTablet]: {
    width: "80%",
  },
  wrapperResponsive: {
    margin: "10px auto 0",
    display: "block",
    maxWidth: "580px",
    width: "100%",
  },
  wrapperEmbed: {
    position: "relative",
    overflow: "hidden",
    borderRadius: "4px",
    [mediaQueries.smallerThan580p]: {
      maxWidth: "80%",
      margin: "0 auto",
    },
  },
  iframeStyle: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: 0,
  },
  loadMoreButton: {
    display: "block",
    width: "200px",
    margin: "20px auto",
    padding: "10px 0",
    backgroundColor: colors.blue8,
    color: "white",
    borderRadius: "0.375rem",
    textAlign: "center",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: colors.blue19,
    },
    "&:disabled": {
      backgroundColor: colors.blue17,
    },
  },
})

const pageImage = PageImage.src

function TpGoogInnerComponent() {
  return <TrustpilotGoogleReviews />
}

export default function ReviewsPage({ initialReviews }) {
  const [reviews, setReviews] = useState(initialReviews.data || [])
  const totalPages = initialReviews.meta?.numberOfPages
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  const classes = useStyles()
  const router = useRouter()
  const { pageNumber = 1 } = router.query
  const host = config.getWebsiteHost()
  const app_host = "https://app.citizenshipper.com"

  const pageURL = `${host}/reviews`

  const pageTitle =
    "Customer Reviews on CitizenShipper: Trusted Reviews for Pet & Item Transportation"
  const pageDescription =
    "Discover real reviews from satisfied customers who've used CitizenShipper for their pet and item transportation needs. From pets to heavy equipment, read about their trusted shipping experiences across the U.S."

  const videoSchema = (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: `{"@context":"http://schema.org/",
                 "@type":"VideoObject",
                 "name":"Real User Reviews: Why Citizen Shipper is the #1 Choice for Pet Shipping!",
                 "contentUrl":"https://www.youtube.com/watch?v=d2Ng53qfkqE",
                 "duration":"PT0H1M10S",
                 "thumbnailUrl":"https://img.youtube.com/vi/d2Ng53qfkqE/maxresdefault.jpg",
                 "uploadDate":"2024-03-25",
                 "description":"Thinking about transporting your pet and seeking genuine feedback? Look no further! In this video, real users share their authentic experiences with Citizen Shipper - the leading platform for pet transportation. From initial hesitations to unforgettable journeys, these reviews shine a light on the reliability and excellence of the service.\n\n🐕 Key Highlights from the Video:\n\n✨ User-Friendly Experience: Multiple users rave about the ease and intuitiveness of Citizen Shipper's website, ensuring a hassle-free booking process.\n✨ Trustworthy Transporters: Discover how our users found top-tier pet transporters, with some even calling their chosen drivers 'phenomenal' and 'out of this world'.\n✨ Exceptional Customer Service: The efficiency and friendliness of the Citizen Shipper staff stand out in the reviews. Whether it's a query or an issue, their team is swift to assist.\n✨ Safety First: One of the predominant themes throughout is the safe delivery of pets. Rest easy knowing that your furry friend is in trusted hands.\n✨ High Recommendations: Almost all users in the video wholeheartedly recommend Citizen Shipper to both friends and strangers alike.\n\nWhy take a gamble with your pet's transportation? Choose a platform backed by genuine positive reviews and a track record of success. Don't forget to bookmark Citizen Shipper for all your pet transportation needs!\n\nJoin the conversation by sharing your experiences in the comments below and remember to give us a thumbs up if this video was helpful. For more reviews and pet-related content, make sure to subscribe to our channel.",
                 "potentialAction" : {
                  "@type": "SeekToAction",
                  "target": "https://www.youtube.com/watch?v=d2Ng53qfkqE&t={seek_to_second_number}s",
                  "startOffset-input": "required name=seek_to_second_number"
                  }
                }
          `,
      }}
    />
  )

  const url = new URL(`${pageURL}`)
  const cleanURL = `${url.origin}${url.pathname}`

  const get404Head = () => {
    return (
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={cleanURL} />
        <meta itemProp="name" content={pageTitle} />
        <meta itemProp="image" content={pageImage} />
        <link rel="next" href={pageNext} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:site" content="@CitizenShipper" />
        <meta name="twitter:image:src" content={pageImage} />
        <meta name="og:title" content={pageTitle} />
        <meta name="og:description" content={pageDescription} />
        <meta name="og:image" content={pageImage} />
        <meta name="og:url" content={pageURL} />
        <meta name="og:site_name" content="CitizenShipper" />
        <meta name="og:type" content="website" />
        <meta name="robots" content="noindex" />
      </Head>
    )
  }

  if (!reviews) {
    return (
      <>
        {get404Head()}
        <ErrorPage statusCode={404} />
      </>
    )
  }
  const generateReviewsStructuredData = (reviews) => {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "CitizenShipper Transportation Marketplace",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        reviewCount: "7879",
        itemReviewed: {
          "@type": "Service",
          name: "Service",
        },
        bestRating: "5",
      },
      review: reviews.data?.map((review) => ({
        "@type": "Review",
        author: {
          "@type": "Person",
          name: review.driver.name,
        },
        datePublished: review.date,
        description: review.content,
        name: review.shipment.title,
        reviewRating: {
          "@type": "Rating",
          bestRating: "5",
          ratingValue: review.rating,
          worstRating: "1",
        },
      })),
    }
  }
  const reviewsStructuredData = JSON.stringify(
    generateReviewsStructuredData(initialReviews),
  )

  const getHead = () => {
    return (
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="image" content={pageImage} />
        <link rel="canonical" href={cleanURL} />
        <meta itemProp="name" content={pageTitle} />
        <meta itemProp="image" content={pageImage} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:site" content="@CitizenShipper" />
        <meta name="twitter:image:src" content={pageImage} />
        <meta name="og:title" content={pageTitle} />
        <meta name="og:description" content={pageDescription} />
        <meta name="og:image" content={pageImage} />
        <meta name="og:url" content={pageURL} />
        <meta name="og:site_name" content="CitizenShipper" />
        <meta name="og:type" content="website" />
        {videoSchema}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: reviewsStructuredData }}
        ></script>
      </Head>
    )
  }

  const fetchMoreReviews = async () => {
    setIsLoading(true)
    const nextPage = currentPage + 1
    const response = await ShipmentService.getReviews(nextPage)
    if (response && response.data) {
      setReviews((prevReviews) => [...prevReviews, ...response.data.data])
      setCurrentPage(nextPage)
    }
    setIsLoading(false)
  }

  return (
    <>
      {getHead()}
      <Navbar
        activeButton={[
          {
            text: "Get Transportation Quotes",
            route: "/shipments/new/",
          },
        ]}
      />
      <div className={classes.mainWrapper}>
        <h1 className={classes.heading}>
          CitizenShipper Customer Reviews:
          <br />
          Real Stories, Transparent Feedback
        </h1>
        <div className={`${classes.wrapperResponsive}`}>
          <div className={classes.wrapperEmbed}>
            <LazyYoutube videoId="d2Ng53qfkqE" />
          </div>
        </div>
        <h2 className={classes.subHeading}>
          See why our customers love CitizenShipper
        </h2>
        <ReviewsContextProvider>
          <TpGoogInnerComponent />
        </ReviewsContextProvider>
        <div
          style={{
            marginTop: "50px",
            marginBottom: "50px",
          }}
        ></div>
        <div>
          <div className={classes.ctaIntroText}>
            <p>Join our Satisfied Community of Customers</p>
          </div>

          <div className={classes.ctaContainer}>
            <Anchor
              href="https://app.citizenshipper.com/shipments/new"
              className={classes.ctaButton}
            >
              Get Free Transport Quotes, Fast
            </Anchor>
            <div className={classes.ctaOutroText}>
              <p>No credit card needed</p>
            </div>
          </div>
        </div>

        <p
          className="text-blue5 text-lg"
          style={{
            marginRight: "auto",
            marginLeft: "auto",
            maxWidth: 600,
            width: "90%",
            textAlign: "center",
          }}
        >
          At CitizenShipper, we pride ourselves on transparency and the value of
          our customer's voices. Every review, whether it's a praise or a
          constructive critique, is shared here.
          <br />
          <br />
          As you navigate through the experiences of those who've used our
          services for transporting pets, motorcycles, or furniture, you'll gain
          a genuine insight into our commitment, safety standards, and
          dedication.
          <br />
          <br />
          Your trust drives us, and these reviews, from all shades of feedback,
          exemplify our promise to serve you.
        </p>
        <h2 className={classes.subHeading}>
          Most Recent Reviews From Our Customers
        </h2>
        <div className={classes.reviews}>
          <div style={{ width: 600 }}>
            <div className={classes.reviewsInnerWrapper}>
              {reviews.map((review, index) => (
                <div key={review.id}>
                  <div key={review.id}>
                    <div className={classes.card}>
                      <div className={classes.overline}>
                        <StarRating value={review.rating} />
                        <span className={classes.overlineText}>
                          Won by{" "}
                          {isValidDriverId(review.driver.id) ? (
                            <Anchor
                              className={classes.driverName}
                              href={`/drivers/${review.driver.id}`}
                            >
                              {review.driver.name}
                            </Anchor>
                          ) : (
                            review.driver.name
                          )}{" "}
                          <span>
                            for{" "}
                            <span className={classes.priceTag}>
                              ${review.winningBid.toFixed(2)}
                            </span>
                          </span>
                        </span>
                      </div>
                      <Anchor
                        href={`${app_host}/shipment-details/${review.shipment.slug}/${review.shipment.id}`}
                        rel="nofollow"
                        className={classes.shipmentTitle}
                      >
                        {review.shipment.title}
                      </Anchor>
                      <p className={classes.text}>{review.content}</p>
                    </div>

                    {index === 2 && (
                      <div>
                        <div className={classes.ctaIntroText}>
                          <p>
                            Experience the CitizenShipper advantage.
                            <br />
                            Swift, cost-free quotes are just a step away.
                          </p>
                        </div>

                        <div className={classes.ctaContainer}>
                          <Anchor
                            href="https://app.citizenshipper.com/shipments/new"
                            className={classes.ctaButton}
                          >
                            Kickstart Your Shipping Journey Now
                          </Anchor>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              className={classes.loadMoreButton}
              onClick={fetchMoreReviews}
              disabled={isLoading || currentPage >= totalPages}
            >
              {isLoading && currentPage < totalPages
                ? "Loading..."
                : "Load More"}
            </button>
            <div className="text-center mt-4">
              <span className="font-bold">
                Page {currentPage} of {totalPages}
              </span>
            </div>
          </div>
        </div>
      </div>
      <Footer noBlok />
    </>
  )
}

export async function getServerSideProps(context) {
  const response = await ShipmentService.getReviews(1)
  if (!response) {
    return {
      notFound: true,
    }
  }
  return {
    props: { initialReviews: response.data },
  }
}
